/**
 * Locale identifiers for the ops-console surface.
 *
 * We ship two locales — `zh-CN` (default, matches product positioning)
 * and `en` (English for international operators and internal QA).
 * Adding a locale is an intentional, breaking change — the dictionary
 * type guarantee (`typeof zhCN` in `en.ts`) means every key must exist
 * in every locale.
 *
 * The persistence key is `ops.locale` — deliberately distinct from
 * console-web's `console.locale` and landing-web's `landing.locale` so
 * each surface persists its own preference independently.
 */
export type Locale = 'zh-CN' | 'en';

/**
 * Runtime list of supported locales, exposed so the switcher can
 * enumerate without re-declaring the set. Kept in display order (zh-CN
 * first because it is the default).
 */
export const SUPPORTED_LOCALES: readonly Locale[] = ['zh-CN', 'en'];

/**
 * Fallback locale when no localStorage preference is present and no
 * `initialLocale` prop is forced by the test rig.
 */
export const DEFAULT_LOCALE: Locale = 'zh-CN';

/**
 * Native-script label for each locale — the name of each language in
 * its OWN language, not the currently-active one. Rendered by the
 * `LocaleSwitcher` so a user who cannot read the active locale can
 * still find their preferred language.
 */
export const LOCALE_NATIVE_LABELS: Readonly<Record<Locale, string>> = {
  'zh-CN': '中文',
  en: 'English',
};

/**
 * Shape of template parameters accepted by `t()`. Templates reference
 * params via `{name}` — see `format.ts` for the substitution rules.
 */
export type TParams = Readonly<Record<string, string | number>>;

/**
 * Local type guard for `Locale`. Used by the provider to validate a
 * `localStorage` read without an `as`-cast.
 */
export function isLocale(value: unknown): value is Locale {
  if (typeof value !== 'string') {
    return false;
  }
  switch (value) {
    case 'zh-CN':
    case 'en':
      return true;
    default:
      return false;
  }
}
